import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "./store";
import { selectNeedsRebuild, selectRebuildHierarchy, selectRebuildHierarchyStatus } from "./project/projectSlice";

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Hook to check if an item needs rebuilding based on the rebuild hierarchy.
 *
 * @param options - Optional object with featureId and/or userStoryId
 * @returns boolean indicating if the item needs rebuilding, or null if hierarchy not loaded
 *
 * @example
 * // Check if product needs rebuild
 * const productNeedsRebuild = useNeedsRebuild();
 *
 * // Check if specific feature needs rebuild
 * const featureNeedsRebuild = useNeedsRebuild({ featureId: 123 });
 *
 * // Check if specific user story needs rebuild
 * const userStoryNeedsRebuild = useNeedsRebuild({ userStoryId: 456 });
 */
export const useNeedsRebuild = (options?: { featureId?: number; userStoryId?: number }): boolean | null => {
    return useAppSelector(state => selectNeedsRebuild(state, options));
};

/**
 * Hook to get the full rebuild hierarchy data
 */
export const useRebuildHierarchy = () => {
    return useAppSelector(selectRebuildHierarchy);
};

/**
 * Hook to get the rebuild hierarchy loading status
 */
export const useRebuildHierarchyStatus = () => {
    return useAppSelector(selectRebuildHierarchyStatus);
};
